// empty-vs-loading — a CLASSE "estado vazio colonizando estado de carregamento".
//
// O sintoma é sempre o mesmo: a tela AFIRMA "não tem nada" enquanto o fetch ainda
// está no ar. A causa também: o render colapsa `data === undefined` (query em voo,
// nunca entregou nada nesta chave) no MESMO ramo que `data = []` (query ASSENTOU e
// o vazio é de verdade). O dado pra distinguir sempre esteve lá; o render é que
// jogava fora. Mesmo desenho já provado no EPG da TV ao Vivo (LiveChannelRow).
//
// A primitiva é uma linha: `data === undefined && isFetching`.
//
// POR QUE `isFetching` (e não `isLoading`, `isPending` ou `!isPending`):
//   - `isLoading === isPending && isFetching`. Com `placeholderData:keepPreviousData`
//     a query reporta `isPending:false` durante o fetch de uma chave NOVA (o `data`
//     visível é o da chave ANTERIOR), então `isLoading` é falso com dado velho na
//     tela — foi exatamente assim que a Busca ficou dizendo "Nenhum resultado" do
//     termo anterior enquanto buscava o novo.
//   - `enabled:false` ⇒ `fetchStatus:'idle'` ⇒ `isFetching:false`. Ou seja, a
//     primitiva já cobre "query desligada" sem guarda extra: desligada NUNCA é
//     pendente, cai no estado vazio (fail-visible), nunca em skeleton preso.
//   - offline (`fetchStatus:'paused'`) também não é `isFetching` → também cai em
//     vazio VISÍVEL. É a direção segura.
//
// TERMINAÇÃO — o contrapeso que não pode ser esquecido: skeleton eterno é PIOR que
// texto errado por 400ms (esta casa já teve um P0 de gate de dados que travava a
// sessão pra sempre, com a suíte verde). `isFetching` só é true enquanto há
// tentativa EM VOO, e o número de tentativas é FINITO em toda query que consome
// este módulo: o default do app é `retry: retryCount`, e `retryCount` é `0 | 1` por
// capability (`packages/device/src/capabilities.ts:329` — `isWeak ? 0 : 1`); as
// queries que sobrescrevem usam literal finito (`retry: 1` no Replay,
// `count < 2` na busca mobile). Esgotadas as tentativas a query vai a `error` com
// `fetchStatus:'idle'` → `isFetching:false` → o estado vazio APARECE. Erro
// terminal, timeout, `enabled:false` e vazio legítimo caem TODOS em "sem
// conteúdo". Nenhum caminho segura o carregando pra sempre.

/** O mínimo que este módulo lê de um `UseQueryResult` (mantém as fns puras e
 *  testáveis sem arrastar o TanStack pro teste). */
export interface PendingQueryState {
  /** `undefined` = esta query NUNCA entregou dado nesta chave. */
  data: unknown
  /** Há fetch EM VOO agora (`fetchStatus === 'fetching'`). */
  isFetching: boolean
}

/**
 * Query SEM `placeholderData`: pendente = nunca entregou dado E ainda está em voo.
 *
 * `undefined`/`null` (query que não existe pra este caso) NÃO é pendente — é a
 * direção fail-visible: sem query, o estado vazio aparece.
 */
export function isQueryPending(q: PendingQueryState | null | undefined): boolean {
  return !!q && q.data === undefined && q.isFetching
}

/**
 * Query COM `placeholderData: keepPreviousData`: o `data` visível pertence à chave
 * ANTERIOR enquanto a nova está em voo, então `data === undefined` não distingue
 * nada e a ÚNICA leitura honesta sobre a chave ATUAL é `isFetching`.
 *
 * Separada de `isQueryPending` de propósito: usar a errada é justamente o bug.
 */
export function isKeyPending(q: { isFetching: boolean } | null | undefined): boolean {
  return !!q && q.isFetching
}

/** Alguma das queries está pendente? OR, não AND — basta UMA em voo pra a tela
 *  não poder afirmar que está vazia. (`.every(isLoading)` no Replay era o AND:
 *  bastava a 1ª das 8 EPGs resolver pra a tela mentir com 7 no ar.) */
export function anyQueryPending(
  qs: readonly (PendingQueryState | null | undefined)[],
): boolean {
  return qs.some((q) => isQueryPending(q))
}

/** Os TRÊS estados de uma lista, nunca dois. */
export type ListState = 'loading' | 'empty' | 'content'

/**
 * Composição única de lista: tem item → mostra; não tem e alguém em voo →
 * carregando; não tem e nada em voo → vazio DE VERDADE.
 *
 * A ORDEM importa e é parte do fix: `itemCount > 0` vem ANTES do teste de
 * pendência. Sem isso, uma lista que cresce por paginação (o `loadMoreDiscovery`
 * do Replay, que dispara EPGs novas ao rolar) trocaria o conteúdo já pintado por
 * uma cortina de carregamento a cada página.
 */
export function listState(input: {
  itemCount: number
  queries: readonly (PendingQueryState | null | undefined)[]
}): ListState {
  if (input.itemCount > 0) return 'content'
  return anyQueryPending(input.queries) ? 'loading' : 'empty'
}

/**
 * Qual query é a DONA de um id, num par de listas alinhadas por índice (o
 * `useQueries` do Replay espelha `discoveryChannels` uma-a-uma), com fallback pra
 * a query dedicada.
 *
 * Existe porque "ler a query ERRADA" é a segunda cara desta classe, e é mais
 * traiçoeira que a primeira: a query dedicada do canal filtrado está
 * `enabled:false` quando esse canal já veio no lote de descoberta — e query
 * desligada reporta `isLoading:false` PRA SEMPRE, então o guard passava sempre e a
 * tela afirmava "canal sem programas" com o fetch do canal ainda no ar. Nenhum
 * gate pega isso: o código lê um campo real de uma query real.
 */
export function pickQueryForId<T>(input: {
  id: string | null
  ids: readonly string[]
  queries: readonly T[]
  fallback: T
}): T | undefined {
  // INVARIANTE: as duas listas são o mesmo `useQueries` visto de dois ângulos, e
  // o índice é a única coisa que as amarra. Se um refactor passar a FATIAR uma
  // delas (`ids` cresce por paginação, `queries` cobre só o lote), `queries[idx]`
  // vira `undefined`, `isQueryPending(undefined)` é `false` e a tela volta a
  // afirmar "sem programas" com o fetch no ar — a PRÓPRIA classe que este módulo
  // fecha, reaberta pela porta lateral, com todo gate e todo teste verdes.
  //
  // Falha ALTO fora de produção pra o desalinhamento morrer no dev/CI de quem o
  // introduziu. Em produção NÃO estoura (nenhum usuário perde a tela por isto) e
  // NÃO vira "pendente": pendente por desalinhamento seria a cortina eterna, o
  // dano OPOSTO e pior. O comportamento seguro é o mesmo de sempre — cai no
  // `fallback`, que é uma query de verdade e portanto assenta e fica VISÍVEL.
  if (input.ids.length !== input.queries.length) {
    const msg =
      '[empty-vs-loading] pickQueryForId: ids e queries desalinhados ' +
      `(${input.ids.length} vs ${input.queries.length}). O par tem que vir do MESMO ` +
      'useQueries, sem fatiar. Caindo no fallback (fail-visible).'
    if (import.meta.env.DEV) throw new Error(msg)
    console.warn(msg)
    return input.fallback
  }
  if (input.id === null) return input.fallback
  const idx = input.ids.indexOf(input.id)
  return idx >= 0 ? input.queries[idx] : input.fallback
}

/**
 * A Busca precisa de DOIS predicados, não de um — e confundi-los quebra coisas
 * OPOSTAS. Este é o de MONTAGEM: o slot de resultado vazio está na tela?
 *
 * Ele é DE PROPÓSITO estável durante o refetch (não olha `isFetching`), porque é
 * este slot que segura o `Focusable` de "Limpar filtros" — o nó em que a PRÓPRIA
 * tela pousa o foco (efeito F-5 da SearchScreen). Desmontá-lo no meio de um
 * refetch deixa a tela sem nenhum `[data-focused]`, ou seja D-pad morto, até o
 * fetch voltar: medido pelo gate de foco em 5/5 rodadas, buraco de ~380ms. O
 * `placeholderData:keepPreviousData` da Busca existe justamente pra segurar essa
 * janela.
 *
 * A lição, que custou um BLOQUEIA: eu tinha consertado o TEXTO usando o predicado
 * de MONTAGEM. Corrigir o que a tela AFIRMA nunca pode mudar QUANDO ela monta.
 */
export function searchSlotMounted(input: {
  enabled: boolean
  hasData: boolean
  totalCount: number
}): boolean {
  return input.enabled && input.hasData && input.totalCount === 0
}

/**
 * E este é o de AFIRMAÇÃO: o slot pode DIZER "Nenhum resultado"?
 *
 * Subconjunto estrito do de montagem — só afirma quando a chave ATUAL assentou.
 *
 * `keepPreviousData` mantém o resultado do termo anterior na tela de propósito
 * (evita o flicker de reload ao trocar chip/filtro) — mas quando esse resultado
 * anterior era VAZIO, o texto "Nenhum resultado" seguia pintado durante o fetch do
 * termo novo, afirmando sobre um termo que ninguém buscou ainda. `isFetching`
 * (via `isKeyPending`) é o que separa os dois. Mesma guarda que a busca mobile já
 * tinha (`mobile/MobileSearch.tsx`).
 *
 * `hasData` continua no conjunto: sem ele, um erro terminal (data `undefined`)
 * passaria a pintar "Nenhum resultado", que é uma afirmação diferente de "deu
 * ruim" — a tela de busca não tem estado de erro próprio.
 */
export function searchIsEmpty(input: {
  enabled: boolean
  hasData: boolean
  totalCount: number
  isFetching: boolean
}): boolean {
  // `isKeyPending(input)` e não `!input.isFetching` solto: é o MESMO predicado, mas
  // nomeado — a regra "com keepPreviousData só `isFetching` fala da chave atual"
  // passa a ter um caller de verdade em vez de virar superfície morta, e quebrar
  // `isKeyPending` passa a quebrar a Busca (que é o que deve acontecer).
  //
  // Composto sobre `searchSlotMounted` de propósito: deixa explícito no código que
  // AFIRMAR é subconjunto de ESTAR MONTADO, e torna impossível a afirmação virar
  // verdadeira num frame em que o slot não existe.
  // `{ isFetching: input.isFetching }` e não `input` cru: passar o bag de 4 campos
  // casava só por tipagem ESTRUTURAL, o que esconde qual campo a chamada usa de
  // verdade. Explícito custa nada e não deixa a próxima pessoa adivinhar.
  return searchSlotMounted(input) && !isKeyPending({ isFetching: input.isFetching })
}
